/* UN Uniforms — theme.js (vanilla, deferred). Ports React behaviours:
   menu drawer, predictive search overlay, scroll reveal, product gallery,
   variant picker, quantity steppers, AJAX cart, add-all-to-cart, sort select. */
(function () {
  'use strict';

  var money = function (cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'SAR'
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  /* ---------- Scroll reveal (replaces framer-motion whileInView) ---------- */
  function initReveal() {
    var els = document.querySelectorAll('.reveal-on-scroll');
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -14% 0px', threshold: 0.15 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Menu drawer ---------- */
  function initMenuDrawer() {
    var openBtn = document.querySelector('[data-menu-open]');
    var drawer = document.querySelector('[data-menu-drawer]');
    if (!openBtn || !drawer) return;
    var closeBtn = drawer.querySelector('[data-menu-close]');
    function open() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    drawer.addEventListener('click', function (e) { if (e.target === drawer) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  }

  /* ---------- Search overlay + predictive search ---------- */
  function initSearch() {
    var openBtn = document.querySelector('[data-search-open]');
    var overlay = document.querySelector('[data-search-overlay]');
    if (!openBtn || !overlay) return;
    var closeBtn = overlay.querySelector('[data-search-close]');
    var input = overlay.querySelector('[data-predictive-input]');
    var results = overlay.querySelector('[data-predictive-results]');
    var timer = null;

    function open() {
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(function () { input && input.focus(); }, 60);
    }
    function close() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });

    if (!input || !results) return;
    input.addEventListener('input', function () {
      clearTimeout(timer);
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      timer = setTimeout(function () {
        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        fetch(root + 'search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=8')
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var products = (data.resources && data.resources.results && data.resources.results.products) || [];
            results.innerHTML = products.map(function (p) {
              return '<a class="search-result" href="' + p.url + '">' +
                (p.featured_image && p.featured_image.url
                  ? '<img src="' + p.featured_image.url + '&width=120" alt="" loading="lazy" width="60" height="60">'
                  : '') +
                '<div><h3>' + p.title + '</h3><p>' + (p.price ? money(parseFloat(p.price) * 100) : '') + '</p></div></a>';
            }).join('');
          })
          .catch(function () { results.innerHTML = ''; });
      }, 250);
    });
  }

  /* ---------- Cart helpers ---------- */
  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('hidden', count === 0);
    });
  }

  function addItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return fetch('/cart.js').then(function (res) { return res.json(); });
    }).then(function (cart) {
      updateCartCount(cart.item_count);
      return cart;
    });
  }

  /* ---------- Add all to cart (complete-the-look) ---------- */
  function initAddAll() {
    document.querySelectorAll('[data-add-all-to-cart]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var ids = (btn.getAttribute('data-variant-ids') || '').split(',').filter(Boolean);
        if (!ids.length) return;
        var original = btn.textContent;
        btn.disabled = true;
        addItems(ids.map(function (id) { return { id: parseInt(id, 10), quantity: 1 }; }))
          .then(function () { btn.textContent = '✓'; })
          .catch(function () { btn.textContent = '!'; })
          .finally(function () {
            setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 1400);
          });
      });
    });
  }

  /* ---------- Quantity steppers ---------- */
  function initQtySteppers() {
    document.addEventListener('click', function (e) {
      var minus = e.target.closest('[data-qty-minus], [data-cart-qty-minus]');
      var plus = e.target.closest('[data-qty-plus], [data-cart-qty-plus]');
      var btn = minus || plus;
      if (!btn) return;
      var stepper = btn.closest('.qty-stepper');
      if (!stepper) return;
      var input = stepper.querySelector('input');
      if (!input) return;
      var min = parseInt(input.min || '1', 10) || 1;
      var val = parseInt(input.value, 10) || min;
      input.value = minus ? Math.max(min, val - 1) : val + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Product gallery thumbs ---------- */
  function initGallery() {
    var gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;
    var main = gallery.querySelector('[data-main-image]');
    gallery.querySelectorAll('[data-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        if (!main) return;
        main.src = thumb.getAttribute('data-image-url');
        main.removeAttribute('srcset');
        gallery.querySelectorAll('[data-thumb]').forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });
  }

  /* ---------- Variant picker ---------- */
  function initVariantPicker() {
    var picker = document.querySelector('[data-variant-picker]');
    if (!picker) return;
    var variants;
    try { variants = JSON.parse(picker.getAttribute('data-product-json')); } catch (e) { return; }
    var form = picker.closest('form');
    var idInput = form && form.querySelector('[data-variant-id]');
    var priceEl = document.querySelector('[data-price]');
    var addBtn = document.querySelector('[data-add-to-cart]');
    var selected = [];

    picker.querySelectorAll('[data-option-index]').forEach(function (group, gi) {
      var active = group.querySelector('.is-active');
      selected[gi] = active ? active.getAttribute('data-option-value') : null;
      group.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-option-value]');
        if (!btn) return;
        group.querySelectorAll('[data-option-value]').forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        selected[gi] = btn.getAttribute('data-option-value');
        var colorLabel = document.querySelector('[data-selected-color]');
        if (colorLabel && group.previousElementSibling) {
          // update colour label if this is a colour group
          var head = group.closest('.option-block');
          if (head && head.querySelector('[data-selected-color]')) {
            head.querySelector('[data-selected-color]').textContent = selected[gi];
          }
        }
        var match = variants.find(function (v) {
          return selected.every(function (val, i) { return !val || v.options[i] === val; });
        });
        if (match) {
          if (idInput) idInput.value = match.id;
          if (priceEl) priceEl.textContent = money(match.price);
          if (addBtn) {
            addBtn.disabled = !match.available;
          }
        }
      });
    });
  }

  /* ---------- AJAX add to cart on product form ---------- */
  function initProductForm() {
    var form = document.getElementById('ProductForm');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('[data-add-to-cart]');
      var original = btn ? btn.textContent : '';
      if (btn) btn.disabled = true;
      var fd = new FormData(form);
      fetch('/cart/add.js', { method: 'POST', body: fd })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return fetch('/cart.js').then(function (res) { return res.json(); });
        })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          if (btn) btn.textContent = btn.getAttribute('data-added-label') || '✓';
        })
        .catch(function () { if (btn) btn.textContent = '!'; })
        .finally(function () {
          setTimeout(function () { if (btn) { btn.textContent = original; btn.disabled = false; } }, 1400);
        });
    });
  }

  /* ---------- Cart page quantity sync ---------- */
  function initCartPage() {
    document.querySelectorAll('[data-cart-qty]').forEach(function (input) {
      input.addEventListener('change', function () {
        var line = input.getAttribute('data-line');
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line, 10), quantity: parseInt(input.value, 10) || 0 })
        })
          .then(function (r) { return r.json(); })
          .then(function () { window.location.reload(); });
      });
    });
  }

  /* ---------- Collection sort select ---------- */
  function initSort() {
    var select = document.querySelector('[data-sort-select]');
    if (!select) return;
    select.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.href = url.toString();
    });
  }

  /* ---------- Collection sidebar search ---------- */
  function initCollectionSearch() {
    var input = document.querySelector('[data-collection-search]');
    if (!input) return;
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        var url = new URL(window.location.pathname, window.location.origin);
        if (input.value.trim()) url.searchParams.set('q', input.value.trim());
        window.location.href = url.toString();
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initReveal();
    initMenuDrawer();
    initSearch();
    initAddAll();
    initQtySteppers();
    initGallery();
    initVariantPicker();
    initProductForm();
    initCartPage();
    initSort();
    initCollectionSearch();
  });
})();

/* ===== Wishlist (localStorage) ===== */
(function () {
  var KEY = 'un_wishlist';
  function getList() { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; } }
  document.addEventListener('click', function (e) {
    var wb = e.target.closest('[data-wishlist]');
    if (!wb) return;
    e.preventDefault(); e.stopPropagation();
    var list = getList();
    var h = wb.getAttribute('data-wishlist');
    var i = list.indexOf(h);
    if (i > -1) { list.splice(i, 1); wb.classList.remove('is-active'); }
    else { list.push(h); wb.classList.add('is-active'); }
    localStorage.setItem(KEY, JSON.stringify(list));
  });
  var wl = getList();
  document.querySelectorAll('[data-wishlist]').forEach(function (b) {
    if (wl.indexOf(b.getAttribute('data-wishlist')) > -1) b.classList.add('is-active');
  });
})();

/* ===== PDP total, buy now, reset ===== */
(function () {
  var form = document.getElementById('ProductForm');
  var totalEl = document.querySelector('[data-total]');
  if (!form || !totalEl) return;
  var picker = document.querySelector('[data-variant-picker]');
  var variants = [];
  try { variants = JSON.parse(picker ? picker.getAttribute('data-product-json') : '[]'); } catch (e) {}
  function basePrice() {
    var idInput = form.querySelector('[data-variant-id]');
    var id = idInput ? parseInt(idInput.value, 10) : 0;
    for (var i = 0; i < variants.length; i++) { if (variants[i].id === id) return variants[i].price; }
    return parseInt(totalEl.getAttribute('data-base-price'), 10) || 0;
  }
  function fmt(cents) {
    var n = cents / 100;
    return 'SAR ' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }
  function recalc() {
    var qtyInput = form.querySelector('input[name="quantity"]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    var extra = 0;
    form.querySelectorAll('[data-custom-option]').forEach(function (c) {
      if (c.checked) extra += parseInt(c.getAttribute('data-custom-price'), 10) || 0;
    });
    totalEl.textContent = fmt((basePrice() + extra) * qty);
  }
  document.addEventListener('click', function () { setTimeout(recalc, 60); });
  document.addEventListener('change', function () { setTimeout(recalc, 60); });
  recalc();
  var buyNow = document.querySelector('[data-buy-now]');
  if (buyNow) {
    buyNow.addEventListener('click', function () {
      buyNow.setAttribute('disabled', 'disabled');
      var fd = new FormData(form);
      fetch('/cart/add.js', { method: 'POST', body: fd })
        .then(function (r) { if (r.ok) { window.location.href = '/checkout'; } else { buyNow.removeAttribute('disabled'); } })
        .catch(function () { buyNow.removeAttribute('disabled'); });
    });
  }
  var reset = document.querySelector('[data-pdp-reset]');
  if (reset) {
    reset.addEventListener('click', function () {
      form.querySelectorAll('[data-custom-option]').forEach(function (c) { c.checked = false; });
      var q = form.querySelector('input[name="quantity"]');
      if (q) { q.value = q.getAttribute('min') || 1; }
      recalc();
    });
  }
})();

/* ===== Customisation conditional panels + file label ===== */
(function () {
  document.addEventListener('change', function (e) {
    var t = e.target.closest('[data-cust-toggle]');
    if (t) {
      var p = document.querySelector('[data-cust-panel="' + t.getAttribute('data-cust-toggle') + '"]');
      if (p) { p.hidden = !t.checked; }
    }
    var f = e.target.closest('.cust-upload') ? e.target : null;
    if (f && f.type === 'file' && f.files && f.files.length) {
      var l = f.closest('.cust-upload').querySelector('[data-file-label]');
      if (l) { l.textContent = f.files[0].name; }
    }
  });
})();
